// AF-3.19 P1 — Creator chain: Review. Real route (AF-3.5), real field
// names verified against creative_review.py's document construction
// (candidate_label, review_type, overall_score, confidence, per-dimension
// scores, strengths/weaknesses/missing_requirements, recommendation,
// selection_status, requires_revision, governance_status).

import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { BrandContextBar } from '@/components/brand/BrandContextBar';
import { StatusBadge, GovernanceBadge, ConfidenceIndicator } from '@/components/brand/Badges';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { EvidenceDrawer } from '@/components/brand/EvidenceDrawer';
import { useBrandContext } from '@/hooks/useBrandContext';
import { creativeReviewAPI } from '@/lib/intelligenceApi';
import { CaretRight } from '@phosphor-icons/react';

const SCORE_DIMENSIONS = [
  ['strategy_fit_score', 'Strategy fit'], ['evidence_fit_score', 'Evidence fit'],
  ['message_quality_score', 'Message quality'], ['creative_distinctiveness_score', 'Distinctiveness'],
  ['experiment_integrity_score', 'Experiment integrity'], ['governance_score', 'Governance'],
  ['readiness_score', 'Readiness'], ['risk_score', 'Risk'],
];

// AF-3.19 Phase 7 — minimal Strategy -> Ideas -> Review -> Decisions ->
// Production -> Distribution stepper, shared shape with the other Creator
// chain pages, so this page's position in the sequence is obvious at a
// glance.
const CHAIN_STAGES = ['Strategy', 'Ideas', 'Review', 'Decisions', 'Production', 'Distribution'];

function ChainProgress({ current }) {
  return (
    <div data-testid="creator-chain-progress" className="flex items-center gap-1.5 flex-wrap mb-3">
      {CHAIN_STAGES.map((stage, i) => (
        <span key={stage} className="flex items-center gap-1.5">
          <span className={`text-[10px] font-mono uppercase ${stage === current ? 'text-[var(--accent)] font-medium' : 'text-[var(--text-tertiary)]'}`}>{stage}</span>
          {i < CHAIN_STAGES.length - 1 && <CaretRight size={10} className="text-[var(--text-tertiary)]" />}
        </span>
      ))}
    </div>
  );
}

export default function CreatorReviewPage() {
  const ctx = useBrandContext();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [evidenceFor, setEvidenceFor] = useState(null);

  const load = useCallback(() => {
    if (!ctx.ready) return;
    setLoading(true); setError(null);
    creativeReviewAPI.list(ctx.workspaceId, { entity_type: ctx.entityType, entity_id: ctx.entityId })
      .then((r) => setReviews(r.data?.reviews || r.data || []))
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [ctx.ready, ctx.workspaceId, ctx.entityType, ctx.entityId]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout>
      <div data-testid="creator-review-page" className="max-w-5xl mx-auto">
        <div className="mb-4">
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Creative Review</h1>
          <p className="text-[12px] text-[var(--text-secondary)]">Scored, ranked candidates awaiting a decision. Ranking is the backend's own — this page never re-scores.</p>
        </div>

        <ChainProgress current="Review" />

        <BrandContextBar ctx={ctx} />

        {!ctx.ready && !ctx.loadingClients && <EmptyState title="Select a workspace and entity" description="Choose a brand workspace and entity above to view its reviews." />}
        {loading && <LoadingState />}
        {error && <ErrorState error={error} onRetry={load} />}

        {!loading && !error && ctx.ready && reviews.length === 0 && (
          <EmptyState title="No candidates reviewed yet" description="Review runs once ideas or media exist for this entity." />
        )}

        {!loading && !error && reviews.length > 0 && (
          <div className="space-y-3" data-testid="creator-review-list">
            {reviews.map((rv) => (
              <div key={rv.id} className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]" data-testid="creator-review-row">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div>
                    <p className="text-[9px] font-mono uppercase text-[var(--text-tertiary)]">{rv.review_type?.replace(/_/g, ' ')}</p>
                    <p className="text-[13px] text-[var(--text-primary)] font-semibold mt-0.5">{rv.candidate_label || 'Candidate'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {rv.rank !== null && rv.rank !== undefined && (
                      <span className="text-[10px] font-mono text-[var(--text-secondary)] border border-[var(--border)] rounded px-1.5 py-0.5">Rank #{rv.rank}</span>
                    )}
                    <StatusBadge value={rv.selection_status} />
                  </div>
                </div>

                <div className="flex items-center gap-4 mb-3">
                  {rv.overall_score !== undefined && rv.overall_score !== null && (
                    <div><p className="text-[9px] font-mono uppercase text-[var(--text-tertiary)]">Overall</p><p className="text-[16px] font-bold text-[var(--text-primary)]">{rv.overall_score}</p></div>
                  )}
                  {rv.confidence !== undefined && rv.confidence !== null && (
                    <div><p className="text-[9px] font-mono uppercase text-[var(--text-tertiary)]">Confidence</p><ConfidenceIndicator value={rv.confidence} /></div>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                  {SCORE_DIMENSIONS.map(([key, label]) => (
                    rv[key] !== undefined && rv[key] !== null ? (
                      <div key={key} className="text-[10px]" data-testid={`review-score-${key}`}>
                        <p className="text-[var(--text-tertiary)] font-mono uppercase">{label}</p>
                        <p className="text-[var(--text-primary)] font-mono">{rv[key]}</p>
                      </div>
                    ) : null
                  ))}
                </div>

                {rv.governance_status && <div className="mb-2"><GovernanceBadge value={rv.governance_status} /></div>}

                {rv.strengths?.length > 0 && (
                  <div className="mb-1.5"><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Strengths</p><p className="text-[11px] text-[var(--text-secondary)]">{rv.strengths.join(', ')}</p></div>
                )}
                {rv.weaknesses?.length > 0 && (
                  <div className="mb-1.5"><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Weaknesses</p><p className="text-[11px] text-[var(--text-secondary)]">{rv.weaknesses.join(', ')}</p></div>
                )}
                {rv.requires_revision && (
                  <div className="mt-2 px-2.5 py-2 rounded bg-[var(--warning-bg)] border border-[var(--warning-border)]">
                    <p className="text-[10px] font-mono uppercase text-[var(--warning)]">Requires revision</p>
                    <p className="text-[11px] text-[var(--text-secondary)]">{(rv.revision_reasons || []).join(', ') || 'See weaknesses above.'}</p>
                  </div>
                )}
                {rv.recommendation && <p className="text-[11px] text-[var(--text-primary)] mt-2">{rv.recommendation}</p>}
                <button data-testid="creator-review-evidence-trigger" className="mt-2 text-[11px] text-[var(--accent)]" onClick={() => setEvidenceFor(rv)}>
                  Why this score? →
                </button>
              </div>
            ))}
          </div>
        )}

        {evidenceFor && (
          <EvidenceDrawer
            open={!!evidenceFor}
            onOpenChange={(open) => !open && setEvidenceFor(null)}
            title="Why Martian scored this candidate"
            evidence={evidenceFor.evidence}
            provenance={{
              creative_strategy_id: evidenceFor.creative_strategy_id ? [evidenceFor.creative_strategy_id] : [],
              creative_idea_id: evidenceFor.creative_idea_id ? [evidenceFor.creative_idea_id] : [],
              creator_artifact_id: evidenceFor.creator_artifact_id ? [evidenceFor.creator_artifact_id] : [],
              media_asset_id: evidenceFor.media_asset_id ? [evidenceFor.media_asset_id] : [],
            }}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
